import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from './Button'
import { useAuth } from 'util/auth'
import { IconBrandYoutubeFilled, IconCloudUpload } from '@tabler/icons-react'
import { InputHeading } from './InputHeading'
import { UploadTask, useUploadStore } from 'hooks/useUploadStore'
import { v4 as uuidv4 } from 'uuid'
import toast from 'react-hot-toast'
import ModalConfirm from './ModalConfirm'
import UploadModal from './UploadingModal'
import { useRouter } from 'next/router'
import { TextField } from './Fields'
import { useSetupStore } from 'hooks/useSetupStore'

export function UploadYouTube({ onUpload }: { onUpload: (data: any) => void }) {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm()
  const auth = useAuth()
  const { addUploads, removeUpload } = useUploadStore()
  const uploads = useUploadStore((state) => state.uploads)
  const router = useRouter()
  const project_id =
    router.query.project_id || useSetupStore.getState().project?.id
  const [videoUrl, setVideoUrl] = useState('')

  const currentUpload = uploads.find(
    (upload) => upload.videoUrl === videoUrl && upload.type === 'youtube'
  )

  const onSubmit = (data) => {
    if (!auth.user) {
      toast.error('User not authenticated.')
      return
    }

    const uploadId = uuidv4()
    const newUpload: UploadTask = {
      id: uploadId,
      videoUrl: data.videoUrl,
      progress: 0,
      status: 'pending',
      userId: auth.user.id,
      type: 'youtube',
      projectId: parseInt(project_id as string) || undefined,
    }
    addUploads([newUpload])
    setVideoUrl('') // Reset the input field if desired
    onUpload?.(newUpload)
  }

  return (
    <>
      {currentUpload &&
        currentUpload.status === 'error' &&
        currentUpload.error && (
          <ModalConfirm
            message={currentUpload.error}
            onConfirm={() => removeUpload(currentUpload.id)}
            title="Whoops!"
            type="error"
            handleClose={() => removeUpload(currentUpload.id)}
          />
        )}
      <div className="w-full">
        <form
          className="flex w-full flex-col items-end gap-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextField
            label={
              <InputHeading
                icon={
                  <IconBrandYoutubeFilled className="h-6 w-6 text-red-500" />
                }
                title="YouTube URL"
                description="Make sure the YouTube video isn't set to private and your link is valid."
              />
            }
            id="videoUrl"
            value={videoUrl}
            {...register('videoUrl', {
              required: 'Please enter the YouTube URL for this video.',
              validate: (value) => {
                try {
                  const url = new URL(value)
                  // Check if the hostname is one of YouTube's
                  if (
                    url.hostname !== 'www.youtube.com' &&
                    url.hostname !== 'youtube.com' &&
                    url.hostname !== 'youtu.be'
                  ) {
                    return 'Please enter a valid YouTube URL.'
                  }
                  // Optionally, check for the presence of the video ID
                  if (
                    url.hostname === 'www.youtube.com' ||
                    url.hostname === 'youtube.com'
                  ) {
                    if (!url.searchParams.get('v')) {
                      return 'The URL does not contain a valid video ID.'
                    }
                  }
                  return true
                } catch (error) {
                  return 'Please enter a valid URL.'
                }
              },
            })}
            className="w-full"
            onChange={(e) => setVideoUrl(e.target.value)}
            type="url"
            required
            placeholder="https://www.youtube.com/watch?v=5Q4Vth5CnPw"
          />
          <div className="flex w-full flex-col-reverse items-center justify-end gap-2 sm:flex-row">
            {errors.videoUrl && (
              <p className="text-xs text-red-500">
                {errors.videoUrl.message?.toString()}
              </p>
            )}
            <Button icon={IconCloudUpload} className="w-fit">
              Import Video
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}
